import clsx from "clsx";
import Link from "next/link";
import type { ComponentProps } from "react";

import { BrandIcon } from "@shared/ui/brand-icon";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import { formatDate } from "@shared/utils/string";

import type { ArticleType } from "@/_pages/article/model";

import s from "./article-card.module.scss";

export type ArticleCardProps = ComponentProps<"div"> & {
  className?: string;
  data: ArticleType;
  /** Уровень заголовка карточки в структуре страницы. По умолчанию h2 — карточки идут сразу под h1 страницы-листинга. */
  titleTag?: "h2" | "h3" | "h4";
  /** Раздел, в котором открывается статья: новости и блог — разные адреса */
  basePath?: "/news" | "/blog";
};

export const ArticleCard = (props: ArticleCardProps) => {
  const {
    className,
    titleTag = "h2",
    basePath = "/news",
    data: { hero, createdAt, documentId, slug, title },
  } = props;

  const href = `${basePath}/${slug}`;
  // hero — необязательный блок: пока редактор его не заполнил, берём заголовок
  // самой записи. Раньше карточка падала на hero.description и роняла листинг
  const cardTitle = hero?.title || title;

  return (
    <article className={clsx(s.root, className)}>
      <Link
        href={href}
        className={s.overlayLink}
        aria-label={hero?.title ?? undefined}
      >
        &nbsp;
      </Link>
      <div className={s.main}>
        <div className={s.top}>
          <Body size="uppercase-small" className={s.info}>
            {createdAt != null && createdAt !== "" && (
              <span>{formatDate(createdAt)}</span>
            )}
            {/* {hero.timeToRead && (
              <>
                <span>&bull;</span>
                <span>{hero?.timeToRead ?? ""} чтения</span>
              </>
            )} */}
          </Body>
          {cardTitle != null && cardTitle !== "" && (
            <Heading level={5} tag={titleTag} className={s.title}>
              {cardTitle}
            </Heading>
          )}
        </div>
        <div className={s.bottom}>
          {hero?.description && (
            <Body size="caption" className={s.description}>
              {hero.description}
            </Body>
          )}
          <ButtonWithIcon
            variant="primary-dark"
            href={href}
            iconName="sur"
            iconFillColor="gold"
            className={s.btn}
          >
            Читать
          </ButtonWithIcon>
        </div>
      </div>
      {hero?.mainImage?.url && (
        <div className={s.imageWrapper}>
          <Image
            src={hero.mainImage.url}
            alt={hero.mainImage?.alternativeText ?? "article card image"}
            className={s.image}
            height={"100%"}
          />
          <div className={s.overlayIconWrapper}>
            <BrandIcon
              className={s.overlayIcon}
              name="line-tree"
              fillColor="cadmium"
              strokeColor="milk"
            />
            <BrandIcon
              className={clsx(s.overlayIcon, s.overlayIconArrow)}
              data-arrow-icon
              name="arrow"
              fillColor="milk"
              strokeColor="cadmium"
            />
          </div>
        </div>
      )}
    </article>
  );
};

ArticleCard.displayName = "ArticleCard";
